const BE_BASE = '/api';
/**
 * @file auth.js
 * Chiamate al backend per autenticazione OAuth2/Azure AD SSO e permessi.
 * - getMe: recupera le info dell'utente autenticato dalla sessione cookie.
 * - logout: invalida la sessione lato server.
 * - getPermissions: recupera i permessi del ruolo corrente dell'utente.
 */

// Recupera le informazioni dell'utente autenticato (email, ruolo, nome) leggendo il JWT dal cookie.
// Ritorna null se l'utente non è autenticato.
export async function getMe() {
  const res = await fetch('/api/auth/me', { credentials: 'include' });
  if (!res.ok) return null;
  const json = await res.json();
  return json.info;
}

// Invia l'access token Azure al backend per validarlo e ricevere in risposta un JWT applicativo
// impostato come cookie httpOnly. Chiamata dopo il redirect MSAL completato con successo.
// La risposta contiene { azureAccessToken: "<jwt>" } il cui payload include il campo exp.
export async function loginWithAzureToken(accessToken) {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ azureAccessToken: accessToken }),
  });
  if (!res.ok) throw new Error(`Login BE fallito: ${res.status}`);
  storeTokenExpiry(accessToken);
}

// Chiave sessionStorage in cui si salva la scadenza reale del token (Unix ms).
const TOKEN_EXPIRY_KEY = 'jwt_expiry';

// Secondi prima della scadenza entro cui si tenta il refresh preventivo.
const REFRESH_THRESHOLD_SECONDS = 120;

/**
 * Decodifica il payload di un JWT (senza verifica firma).
 * @param {string} token
 * @returns {object|null}
 */
function decodeJwtPayload(token) {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(part));
  } catch {
    return null;
  }
}

/**
 * Legge il campo exp dal JWT e lo salva in sessionStorage (convertito in ms).
 * @param {string} jwt
 */
function storeTokenExpiry(jwt) {
  const payload = decodeJwtPayload(jwt);
  if (payload?.exp) {
    sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(payload.exp * 1000));
  }
}

/** Rimuove la scadenza dal sessionStorage (da chiamare al logout). */
export function clearTokenExpiry() {
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
}

/**
 * Ritorna true se il token scade entro REFRESH_THRESHOLD_SECONDS.
 */
function isTokenNearExpiry() {
  const raw = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!raw) return false;
  const secondsLeft = (Number(raw) - Date.now()) / 1000;
  return secondsLeft > 0 && secondsLeft <= REFRESH_THRESHOLD_SECONDS;
}

// Invalida la sessione lato server cancellando il cookie JWT.
export async function logout() {
  clearTokenExpiry();
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
}

// Refresha la sessione: valido solo se il token è ancora valido ma prossimo alla scadenza.
// Il backend aggiorna il cookie JWT. La risposta deve contenere il nuovo JWT per aggiornare exp.
export async function refreshSession() {
  const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
  if (res.ok) {
    const json = await res.json().catch(() => null);
    const jwt = json?.azureAccessToken ?? json?.info?.azureAccessToken;
    if (jwt) storeTokenExpiry(jwt);
  }
  return res.ok;
}

/**
 * Wrapper attorno a fetch con logica di refresh preventivo e reindirizzamento al login su 401.
 *
 * Flusso:
 * 1. Se il token scade entro REFRESH_THRESHOLD_SECONDS, chiama /auth/refresh e aggiorna exp.
 * 2. Esegue la chiamata originale.
 * 3. Se la risposta è 401 (token scaduto), reindirizza a /login.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  const opts = { credentials: 'include', ...options };

  if (isTokenNearExpiry()) {
    await refreshSession();
  }

  const res = await fetch(url, opts);

  if (res.status === 401) {
    clearTokenExpiry();
    window.location.href = '/login';
    return res;
  }

  return res;
}

/**
 * Recupera i permessi dell'utente autenticato dal backend per il ruolo specificato.
 *
 * Il backend restituisce un oggetto con SOLO i permessi concessi (valore true).
 * I permessi non presenti nella risposta devono essere considerati negati.
 *
 * Esempio risposta BE:
 *   { "canEditCells": true, "canCopy": true, "canPaste": true }
 *
 * In questo esempio canDeleteRow, canClearCells, canEditNote, canMerge sono
 * implicitamente negati perché non presenti nell'oggetto.
 *
 * @param {string} role - Ruolo per cui caricare i permessi (es. 'admin', 'editor')
 * @returns {Promise<Object|null>} Oggetto permessi (solo chiavi true) o null in caso di errore.
 */
export async function getPermissions(role) {
  const res = await fetch(`${BE_BASE}/Permessi/getPermissions?ruolo=${encodeURIComponent(role)}`, { credentials: 'include' });
  if (!res.ok) return null;
  const json = await res.json();
  return json.info;
}
